import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { updateLevelProgress, getLevelHighScore } from '@/config/progress';
import type { Grade } from '@/config/scoring';
import { getRandomMessage } from '@/config/messages';
import type { MultiplayerResultData } from '@/types/multiplayer';
import { ensureDiscordAccessToken, getDiscordGuildId, setDiscordMultiplayerResultsPresence, setDiscordSoloResultsPresence } from '@/discord';
import { fetchAccountStatus, shouldOfferSaveProgress } from '@/services/accountStatus';
import { mountAccountBadge } from '@/ui/accountBadge';
import { createButton, createModalButton } from '@/ui/buttons';
import { SaveProgressModal } from '@/ui/Modal';
import { isDiscordActivity } from '@/utils/discordActivity';
import { submitLeaderboardRun } from '@/services/leaderboard';
import { syncLevelProgressToSupabase } from '@/services/progressSync';
import { getClientVersion } from '@/utils/version';
import { isDebugEnabled } from '@/online/debug';
import { getOnlineRoom, clearOnlineRoom } from '@/online/session';
import { parseRoomStateView } from '@/online/roomState';
import { getLocalSeatColor } from '@/utils/seatColors';
import type { JudgeResult } from '@/systems/performanceJudge';
import { captureError } from '@/sentry';
import { AudioManager } from '@/systems/AudioManager';
import { trackPlayAgain, trackGameComplete, trackMatchComplete } from '@/services/analytics';

const RESULT_BUTTON_SIZES = {
  primary: { width: 200, height: 56, fontSize: THEME.sizes.bodyLarge },
};

interface ResultSceneData {
  score: number;
  streak: number;
  decodedMessage: string;
  intendedMessage: string;
  accuracy: number;
  grade: Grade;
  judge?: JudgeResult;
  levelIndex?: number;
  difficulty?: string;
}

export class ResultScene extends Phaser.Scene {
  private buttonsEnabled: boolean = false;
  private levelIndex: number | null = null;
  private levelSuccess: boolean = false;
  private newHighScore: boolean = false;
  private unlockedLevel: number | null = null;
  private multiplayerData: MultiplayerResultData | null = null;
  private saveModal: SaveProgressModal | null = null;
  private saveButton: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'ResultScene' });
  }

  private setButtonEnabled(btn: Phaser.GameObjects.Container | null, enabled: boolean): void {
    if (!btn) return;
    btn.iterate((obj: Phaser.GameObjects.GameObject) => {
      if (obj instanceof Phaser.GameObjects.Rectangle) {
        if (enabled) obj.setInteractive({ useHandCursor: true });
        else obj.disableInteractive();
      }
      if (obj instanceof Phaser.GameObjects.Text) {
        obj.setAlpha(enabled ? 1 : 0.45);
      }
    });
    btn.setAlpha(enabled ? 1 : 0.5);
  }

  init(data: ResultSceneData | MultiplayerResultData): void {
    this.buttonsEnabled = false;
    this.saveModal = null;

    if ('setup' in data) {
      this.multiplayerData = data;
      this.levelIndex = null;
      this.levelSuccess = false;
      this.newHighScore = false;
      this.unlockedLevel = null;
      return;
    }

    this.multiplayerData = null;
    this.data.set('score', data.score || 0);
    this.data.set('streak', data.streak || 0);
    this.data.set('decodedMessage', data.decodedMessage || '');
    this.data.set('intendedMessage', data.intendedMessage || '');
    this.data.set('accuracy', data.accuracy || 0);
    this.data.set('grade', data.grade || 'D');
    this.data.set('judge', data.judge ?? null);
    this.levelIndex = data.levelIndex ?? null;
    this.data.set('difficulty', data.difficulty || '');

    // Determine if level was completed successfully (ignore whitespace differences)
    const normalizeMessage = (s: string) => s.replace(/\s+/g, '');
    this.levelSuccess = normalizeMessage(data.decodedMessage) === normalizeMessage(data.intendedMessage);

    // Update progress and check for unlocks
    if (this.levelIndex !== null) {
      const grade = (typeof data.grade === 'string' ? data.grade : 'D') as Grade;
      const result = updateLevelProgress(
        this.levelIndex,
        data.score || 0,
        this.levelSuccess,
        grade,
        data.accuracy || 0
      );
      this.newHighScore = result.newHighScore;
      this.unlockedLevel = result.unlockedLevel;
      if (result.newHighScore || result.completionRecorded) {
        void syncLevelProgressToSupabase(this.levelIndex);
      }
    } else {
      this.newHighScore = false;
      this.unlockedLevel = null;
    }
  }

  create(): void {
    // Discord Activity presence
    if (this.multiplayerData?.setup.online?.enabled) {
      // Online multiplayer scores
      setDiscordMultiplayerResultsPresence({
        mode: this.multiplayerData.setup.mode,
        roomId: this.multiplayerData.setup.online.roomId,
        playerCount: this.multiplayerData.players.length,
      });
    } else if (!this.multiplayerData) {
      // Solo results
      setDiscordSoloResultsPresence();
    }

    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Arcade background
    drawArcadeBackground(this);
    mountAccountBadge(this);

    if (this.multiplayerData) {
      this.createMultiplayerResults(this.multiplayerData);
      return;
    }

    const score = this.data.get('score') as number;
    const streak = this.data.get('streak') as number;
    const decodedMessage = this.data.get('decodedMessage') as string;
    const intendedMessage = this.data.get('intendedMessage') as string;
    const accuracy = this.data.get('accuracy') as number;
    const grade = this.data.get('grade') as string;
    const difficulty = (this.data.get('difficulty') as string | null) ?? '';
    const judge = (this.data.get('judge') as JudgeResult | null) ?? null;

    const leftColumnWidth = 560;
    const rightColumnWidth = GAME_WIDTH - leftColumnWidth;
    const leftCenterX = leftColumnWidth / 2 + 160;
    const rightCenterX = leftColumnWidth + rightColumnWidth / 2 - 20;

    // Title with glow - different message based on success
    const titleText = this.levelSuccess ? 'TRANSMISSION COMPLETE!' : 'TRANSMISSION FAILED';
    const titleColor = this.levelSuccess ? THEME.colors.green : THEME.colors.hit.ok;
    const title = this.add.text(GAME_WIDTH / 2, 70, titleText, {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: titleColor,
    }).setOrigin(0.5);
    addTextGlow(title, titleColor, 15);

    // Track game completion
    trackGameComplete({
      mode: 'solo',
      levelIndex: this.levelIndex ?? undefined,
      score,
      grade,
      success: this.levelSuccess,
    });

    // Score with high score underneath
    const scoreY = 140;
    this.add.text(leftCenterX, scoreY, `SCORE: ${score.toLocaleString()}`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.score,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // High score display (always show, right below score)
    const highScore = this.levelIndex !== null ? getLevelHighScore(this.levelIndex) : 0;
    this.add.text(leftCenterX, scoreY + 40, `HIGH SCORE: ${highScore > 0 ? highScore.toLocaleString() : '—'}`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    if (this.newHighScore) {
      AudioManager.get(this).playSfx('high_score');
      const newHighText = this.add.text(leftCenterX, scoreY + 70, 'NEW HIGH SCORE!', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.yellow,
      }).setOrigin(0.5);
      addTextGlow(newHighText, THEME.colors.yellow, 10);
    }

    this.maybeSubmitLeaderboard({
      score,
      accuracy,
      grade,
      maxStreak: streak,
      difficulty,
    });

    // Stats row
    this.add.text(leftCenterX, 250, `STREAK ${streak}    ACCURACY ${accuracy.toFixed(2)}%    GRADE ${grade}`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    // Message comparison
    this.add.text(leftCenterX, 295, 'TARGET MESSAGE:', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(leftCenterX, 322, intendedMessage, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(leftCenterX, 375, 'TRANSMITTED MESSAGE:', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Highlight differences
    const decodedColor = decodedMessage === intendedMessage ? THEME.colors.primary : THEME.colors.hit.ok;
    this.add.text(leftCenterX, 402, decodedMessage, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: decodedColor,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Level unlock notification
    if (this.unlockedLevel) {
      const unlockText = this.add.text(leftCenterX, 455, 'NEXT LEVEL UNLOCKED!', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.cyan,
      }).setOrigin(0.5);
      addTextGlow(unlockText, THEME.colors.cyan, 10);
    }

    this.createJudgeDebugOverlay(judge);

    const samuelY = 255;
    const samuelLabelY = 410;
    const samuelTextY = 435;
    const samuelImage = this.add.image(rightCenterX, samuelY, 'samuel-morse').setOrigin(0.5);
    const maxImageWidth = rightColumnWidth - 60;
    const maxImageHeight = 260;
    const scale = Math.min(
      maxImageWidth / samuelImage.width,
      maxImageHeight / samuelImage.height,
      1
    );
    samuelImage.setScale(scale);
    samuelImage.setTint(0xffffff);

    this.add.text(rightCenterX, samuelLabelY, 'SAMUEL SAYS', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    const noHitsMessages = [
      'I invented the telegraph for THIS?',
      'Did you fall asleep at the key?',
      'Even a broken telegraph makes more noise.',
      '...hello? Is this thing on?',
      'The silence is historically inaccurate.',
    ];
    var samuelAdvice = judge?.selected?.advice
      ?? noHitsMessages[Math.floor(Math.random() * noHitsMessages.length)];
    this.add.text(rightCenterX, samuelTextY, samuelAdvice, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      align: 'center',
      wordWrap: { width: rightColumnWidth - 80 },
    }).setOrigin(0.5, 0);

    // Buttons (delayed to prevent accidental clicks)
    this.time.delayedCall(500, () => {
      this.buttonsEnabled = true;

      const leftButtonText = this.levelSuccess ? 'PLAY NEXT' : 'TRY AGAIN';
      const nextLevelIndex = this.levelSuccess && this.levelIndex !== null ? this.levelIndex + 1 : this.levelIndex;

      createButton(this, {
        x: GAME_WIDTH / 2 - 220,
        y: GAME_HEIGHT - 100,
        text: leftButtonText,
        buttonType: 'normal',
        onClick: () => {
          if (!this.buttonsEnabled) return;
          this.transitionTo('LevelSelectScene', nextLevelIndex !== null ? {
            levelIndex: nextLevelIndex,
          } : undefined);
        },
        style: 'primary',
        sizes: RESULT_BUTTON_SIZES,
      });

      // Save Progress is visible but only enabled for anonymous users.
      this.saveButton = this.createSaveProgressButton(GAME_WIDTH / 2, GAME_HEIGHT - 100, false);
      fetchAccountStatus().then((status) => {
        this.setButtonEnabled(this.saveButton, shouldOfferSaveProgress(status));
      }).catch((error) => {
        captureError(error, { scope: 'result', action: 'fetchAccountStatus' });
        this.setButtonEnabled(this.saveButton, false);
      });

      createButton(this, {
        x: GAME_WIDTH / 2 + 220,
        y: GAME_HEIGHT - 100,
        text: 'MAIN MENU',
        buttonType: 'normal',
        onClick: () => {
          if (this.buttonsEnabled) {
            this.transitionTo('MenuScene');
          }
        },
        style: 'primary',
        sizes: RESULT_BUTTON_SIZES,
      });
    });

    // Also allow keyboard navigation after delay
    this.time.delayedCall(500, () => {
      this.input.keyboard?.on('keydown-ENTER', () => {
        if (this.buttonsEnabled) {
          this.transitionTo('LevelSelectScene');
        }
      });
      this.input.keyboard?.on('keydown-ESC', () => {
        if (this.buttonsEnabled) {
          this.transitionTo('MenuScene');
        }
      });
    });
  }

  private maybeSubmitLeaderboard(payload: {
    score: number;
    accuracy: number;
    grade: string;
    maxStreak: number;
    difficulty: string;
  }): void {
    if (!isDiscordActivity()) return;
    if (this.levelIndex === null || this.levelIndex === undefined) return;

    const guildId = getDiscordGuildId();
    if (!guildId) return;

    void (async () => {
      const accessToken = await ensureDiscordAccessToken();
      if (!accessToken) return;

      const { hash } = getClientVersion();
      await submitLeaderboardRun({
        guildId,
        leaderboardType: `single:${this.levelIndex}`,
        score: payload.score,
        discordAccessToken: accessToken,
        metadata: {
          accuracy: payload.accuracy,
          grade: payload.grade,
          maxStreak: payload.maxStreak,
          difficulty: payload.difficulty || null,
          messageIndex: this.levelIndex,
          buildHash: hash || 'unknown',
        },
      });
    })();
  }

  private createMultiplayerResults(data: MultiplayerResultData): void {
    trackMatchComplete(data.setup.mode, data.players.length);

    const titleText = data.mode === 'telephone' ? 'MESSAGE CHAIN COMPLETE!' : 'TRANSMISSION COMPLETE!';
    const titleColor = THEME.colors.cyan;
    const title = this.add.text(GAME_WIDTH / 2, 70, titleText, {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: titleColor,
    }).setOrigin(0.5);
    addTextGlow(title, titleColor, 15);

    const messageLabel = data.mode === 'telephone' ? 'ORIGINAL:' : 'TARGET:';
    this.add.text(GAME_WIDTH / 2, 135, `${messageLabel} ${this.truncate(data.originalMessage, 42)}`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    const totalPlayers = data.setup.players.length;
    const localSeatIndices = data.setup.online?.localSeatIndices
      ?? Array.from({ length: totalPlayers }, (_, i) => i);

    const startY = 210;
    const rowHeight = 54;

    const ordered = data.mode === 'coop'
      ? [...data.players].sort((a, b) => b.score - a.score)
      : [...data.players].sort((a, b) => a.playerId - b.playerId);

    ordered.forEach((p, index) => {
      const y = startY + index * rowHeight;

      const borderColor = getLocalSeatColor(p.playerId, totalPlayers, localSeatIndices)
        ?? THEME.colors.borderHex;
      const rowBg = this.add.graphics();
      rowBg.fillStyle(THEME.colors.backgroundLightHex, 0.72);
      rowBg.fillRoundedRect(GAME_WIDTH / 2 - 490, y - 22, 980, 44, 14);
      rowBg.lineStyle(2, borderColor);
      rowBg.strokeRoundedRect(GAME_WIDTH / 2 - 490, y - 22, 980, 44, 14);

      const left = data.mode === 'coop' ? `#${index + 1}` : `#${p.playerId + 1}`;
      const label = `${left}  ${p.name}`;
      this.add.text(GAME_WIDTH / 2 - 450, y, label, {
        fontFamily: THEME.fonts.pixelBold,
        fontSize: THEME.sizes.body,
        color: THEME.colors.text,
      }).setOrigin(0, 0.5);

      const message = data.mode === 'telephone'
        ? this.truncate(p.decodedMessage, 26)
        : '';

      if (message) {
        this.add.text(GAME_WIDTH / 2 - 210, y, `"${message}"`, {
          fontFamily: THEME.fonts.mono,
          fontSize: THEME.sizes.body,
          color: THEME.colors.primary,
        }).setOrigin(0, 0.5);
      }

      const stats = `${p.score.toLocaleString()} pts   ${p.accuracy.toFixed(2)}% ${p.grade}   STREAK ${p.maxStreak}`;
      this.add.text(GAME_WIDTH / 2 + 450, y, stats, {
        fontFamily: THEME.fonts.pixel,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
      }).setOrigin(1, 0.5);
    });

    this.maybePlayMultiplayerHighScore(data);

    this.time.delayedCall(500, () => {
      this.buttonsEnabled = true;

      const isOnline = Boolean(data.setup.online?.enabled);

      if (!isOnline) {
        createButton(this, {
          x: GAME_WIDTH / 2 - 120,
          y: GAME_HEIGHT - 100,
          text: 'PLAY AGAIN',
          buttonType: 'normal',
          onClick: () => {
            if (!this.buttonsEnabled) return;
            trackPlayAgain();
            const setup = data.setup;
            const nextScene = setup.mode === 'telephone' ? 'TelephoneGameScene' : 'CoopGameScene';
            this.transitionTo(nextScene, { ...setup, message: getRandomMessage() });
          },
          style: 'primary',
          sizes: RESULT_BUTTON_SIZES,
        });

        createButton(this, {
          x: GAME_WIDTH / 2 + 120,
          y: GAME_HEIGHT - 100,
          text: 'MAIN MENU',
          buttonType: 'normal',
          onClick: () => {
            if (this.buttonsEnabled) {
              this.transitionTo('MenuScene');
            }
          },
          style: 'primary',
          sizes: RESULT_BUTTON_SIZES,
        });
        return;
      }

      // For online matches, add return to lobby and leave options
      const room = getOnlineRoom();
      if (room) {
        createButton(this, {
          x: GAME_WIDTH / 2 - 110,
          y: GAME_HEIGHT - 100,
          text: 'RETURN TO LOBBY',
          buttonType: 'normal',
          onClick: () => {
            if (!this.buttonsEnabled) return;
            // Server auto-transitions to lobby after match_ended, just navigate locally
            this.scene.start('LobbyScene', { role: this.isHost(room) ? 'host' : 'guest' });
          },
          style: 'primary',
          sizes: { primary: { width: 220, height: 56, fontSize: THEME.sizes.body } },
        });

        createButton(this, {
          x: GAME_WIDTH / 2 + 160,
          y: GAME_HEIGHT - 100,
          text: 'LEAVE',
          buttonType: 'normal',
          onClick: () => {
            if (!this.buttonsEnabled) return;
            room.leave();
            clearOnlineRoom();
            this.transitionTo('MenuScene');
          },
          style: 'secondary',
          sizes: { secondary: { width: 90, height: 48, fontSize: THEME.sizes.small } },
        });
      } else {
        createButton(this, {
          x: GAME_WIDTH / 2,
          y: GAME_HEIGHT - 100,
          text: 'MAIN MENU',
          buttonType: 'normal',
          onClick: () => {
            if (this.buttonsEnabled) {
              this.transitionTo('MenuScene');
            }
          },
          style: 'primary',
          sizes: RESULT_BUTTON_SIZES,
        });
      }
    });
  }

  private truncate(value: string, max: number): string {
    if (value.length <= max) return value;
    return `…${value.slice(-max)}`;
  }

  private maybePlayMultiplayerHighScore(data: MultiplayerResultData): void {
    const localSeats = data.setup.online?.localSeatIndices ?? data.players.map((player) => player.playerId);
    if (localSeats.length === 0) return;
    const topScore = Math.max(...data.players.map((player) => player.score));
    if (topScore <= 0) return;
    const localHasTopScore = data.players.some((player) => localSeats.includes(player.playerId) && player.score === topScore);
    if (localHasTopScore) {
      AudioManager.get(this).playSfx('high_score');
    }
  }

  private createSaveProgressButton(x: number, y: number, enabled: boolean): Phaser.GameObjects.Container {
    const btn = createModalButton(this, {
      x,
      y,
      text: 'SAVE PROGRESS',
      onClick: () => this.openSaveModal(),
      style: 'secondary',
    });
    this.setButtonEnabled(btn, enabled);
    return btn;
  }

  private isHost(room: ReturnType<typeof getOnlineRoom>): boolean {
    if (!room) return false;
    const state = parseRoomStateView(room.state);
    return state.hostSessionId === room.sessionId;
  }

  private createJudgeDebugOverlay(judge: JudgeResult | null): void {
    if (!isDebugEnabled()) return;

    const lines: string[] = ['JUDGE DEBUG'];

    if (!judge) {
      lines.push('no judge data');
    } else {
      const fmtVal = (v: number) => (v >= 0 ? ' ' : '') + v.toFixed(1);
      const fmtScore = (v: number) => v.toFixed(1);

      if (judge.selected) {
        lines.push(`top: ${judge.selected.id}  score: ${fmtScore(judge.selected.score)}`);
        lines.push(`advice: ${judge.selected.advice}`);
      } else {
        lines.push('top: none');
      }

      lines.push('');
      lines.push('factors:');
      for (const f of judge.factors) {
        // Format: id  val:X  xM  → S
        const idPad = f.id.padEnd(22);
        const valStr = `val:${fmtVal(f.value)}`;
        const multStr = `x${f.multiplier}`;
        const scoreStr = fmtScore(f.score);
        lines.push(`- ${idPad} ${valStr}  ${multStr}  → ${scoreStr}`);
      }
    }

    const textX = 20;
    const textY = 20;
    const text = this.add.text(textX, textY, lines.join('\n'), {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    }).setOrigin(0, 0);

    const padding = 10;
    const bg = this.add.rectangle(
      textX - padding,
      textY - padding,
      text.width + padding * 2,
      text.height + padding * 2,
      0x000000,
      0.65
    ).setOrigin(0, 0);
    bg.setDepth(0);
    text.setDepth(1);
  }

  private openSaveModal(): void {
    if (this.saveModal) return;
    this.saveModal = new SaveProgressModal({
      scene: this,
      onSuccess: () => {
        // After successful OTP, disable the save button since user is now signed in
        this.setButtonEnabled(this.saveButton, false);
      },
      onDestroy: () => { this.saveModal = null; }
    });
  }

  private transitionTo(scene: string, data?: object): void {
    this.buttonsEnabled = false;
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }
}
